/**
 * Generic function to create FormData from an object
 * @param data - The data object to be converted to FormData
 * @param additionalFields - Optional additional key-value pairs to be included in the FormData
 * @returns FormData
 */
export const createFormData = <T extends Record<string, any>>(
  data: T,
  additionalFields: Record<string, string | undefined> = {}
): FormData => {
  const formData = new FormData();
  const allFields = { ...data, ...additionalFields };

  Object.entries(allFields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    // 1. File vs Blob
    if (value instanceof File) {
      formData.append(key, value, value.name);
    } else if (value instanceof Blob) {
      formData.append(key, value);
    }

    // 2. Arrays
    else if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v instanceof File) {
          formData.append(`${key}[]`, v, v.name);
        } else if (v instanceof Blob) {
          formData.append(`${key}[]`, v);
        } else {
          formData.append(`${key}[]`, String(v));
        }
      });
    }

    // 3. Plain Objects (JSON)
    else if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    }

    // 4. Primitives (string, number, boolean)
    else {
      formData.append(key, String(value));
    }
  });

  return formData;
};

/**
 * Converts an array of objects into FormData.
 *
 * @param data - Array of objects to be converted into FormData.
 * @param keyName - The base key name for nesting in FormData (e.g., "education").
 * @returns FormData
 */
export const convertArrayToFormData = (
  data: Array<Record<string, any>>,
  keyName: string
): FormData => {
  const formData = new FormData();
  data.forEach((item, index) => {
    Object.keys(item).forEach((key) => {
      const value = item[key] !== null ? item[key] : ""; // Replace null with an empty string
      formData.append(`${keyName}[${index}][${key}]`, value);
    });
  });
  return formData;
};
